// AFPES R3 DEV-only proposal: secure Pro/Enterprise completion and recovery.
// Existing Risk Report/Global Pro snapshot behavior is not modified.
import { supabase } from "./supabaseClient";
import { appendAuditSnapshot } from "./auditHistory";

const PENDING_PREFIX = "aifine_pending_paid_completion_r3_";
const FUNCTION = "paid_assessment_finalize";
const UUID_V4 = /^[\da-f]{8}-[\da-f]{4}-4[\da-f]{3}-[89ab][\da-f]{3}-[\da-f]{12}$/i;
export const requiresPaidQuota = (planKey) => planKey === "pro" || planKey === "enterprise";

// sessionStorage separates distinct tabs; the same tab retains its id through
// refresh/retry. A previous successful submission is recoverable from the server
// even after the browser is closed or local storage is lost.
export function getOrCreatePendingAssessmentId(businessId) {
  const key = `${PENDING_PREFIX}${businessId || "single_default"}`;
  const previous = sessionStorage.getItem(key);
  if (previous && UUID_V4.test(previous)) return previous;
  if (!globalThis.crypto?.randomUUID) throw new Error("Secure assessment identity is unavailable.");
  const id = globalThis.crypto.randomUUID();
  sessionStorage.setItem(key, id); // Fail closed rather than lose the retry key.
  if (sessionStorage.getItem(key) !== id) throw new Error("Assessment retry identity was not retained.");
  return id;
}
export function clearPendingAssessmentId(businessId, id) {
  const key = `${PENDING_PREFIX}${businessId || "single_default"}`;
  if (sessionStorage.getItem(key) === id) {
    sessionStorage.removeItem(key);
    if (sessionStorage.getItem(key) === id) throw new Error("Confirmed retry identity could not be cleared.");
  }
}

function responseCode(error, data) {
  if (data?.code) return data.code;
  const status = error?.context?.status;
  return status === 404 ? "NOT_FOUND" : "COMPLETION_NOT_CONFIRMED";
}
async function findCompleted(assessmentId) {
  const { data, error } = await supabase.functions.invoke(`${FUNCTION}?assessment_id=${encodeURIComponent(assessmentId)}`, { method: "GET" });
  if (error || !data || data.status !== "already_completed") return null;
  if (data.snapshot?.snapshot_id !== assessmentId) return null;
  return { status: "already_completed", snapshot: data.snapshot, next_reset: data.next_reset || null };
}

export async function finalizePaidAssessment({ planKey, businessId, assessmentId, snapshot, answers }) {
  let data = null;
  let error = null;
  try {
    const response = await supabase.functions.invoke(FUNCTION, {
      method: "POST", body: {
        plan_key: planKey, business_id: businessId,
        assessment_id: assessmentId, snapshot, answers,
      },
    });
    data = response.data;
    error = response.error;
  } catch (err) { error = err; }
  if (!error && ["completed", "already_completed"].includes(data?.status) &&
      data.snapshot?.snapshot_id === assessmentId) return data;

  let details = data && typeof data === "object" ? data : null;
  if (!details && error?.context && typeof error.context.json === "function") {
    try { details = await error.context.json(); } catch { details = null; }
  }
  const code = responseCode(error, details);
  if (code === "QUOTA_EXHAUSTED") {
    throw new Error(`Your plan's ${details?.allowed ?? "current"} completed Assessment allowance is exhausted. Existing reports remain available.`);
  }
  // The server might have committed before the response or network failed.
  // Query by the SAME durable attempt id before telling the user it failed.
  const recovered = await findCompleted(assessmentId).catch(() => null);
  if (recovered) return recovered;
  throw new Error(`Completion not confirmed (${code}). Your answers and retry identity remain saved; please retry.`);
}

// Retrieve every accessible page, rather than silently hiding older records.
// Cache writes do not establish server completion, which was already recorded.
export async function hydrateServerPaidAssessments() {
  const { data: identity, error: authError } = await supabase.auth.getUser();
  if (authError || !identity?.user) return { count: 0, usage: null, incomplete: false };
  let count = 0;
  let offset = 0;
  let usage = null;
  let incomplete = false;
  const visited = new Set();
  for (let page = 0; page < 1000; page += 1) {
    if (visited.has(offset)) { incomplete = true; break; }
    visited.add(offset);
    const { data, error } = await supabase.functions.invoke(`${FUNCTION}?offset=${offset}`, { method: "GET" });
    if (error || !Array.isArray(data?.snapshots)) { incomplete = true; break; }
    if (page === 0) usage = data.usage || null;
    for (const snapshot of data.snapshots) {
      if (snapshot?.source !== "detailed_assessment" || !snapshot.snapshot_id) continue;
      try { appendAuditSnapshot(snapshot); count += 1; }
      catch { incomplete = true; } // Continue recovering other records.
    }
    if (data.next_offset === null || data.next_offset === undefined) break;
    if (!Number.isSafeInteger(data.next_offset) || data.next_offset <= offset) {
      incomplete = true; break;
    }
    offset = data.next_offset;
    if (page === 999) incomplete = true;
  }
  return { count, usage, incomplete };
}
